import { downloadMarkdownTreeZip, downloadPdfTree } from "../../../../_UI/DocumentExport";

const onText = (value) => String(value ?? "").trim();

const onLines = (pairs = []) => pairs
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([label, value]) => `**${label}:** ${String(value).trim()}`)
    .join("\n\n");

const onDate = (value) => value
    ? new Date(value).toLocaleString("es-AR", { dateStyle: "medium", timeStyle: "short" })
    : "";

const onModuleContent = (module) => [
    onText(module?.description),
    onText(module?.objectives) ? `## Objetivos\n\n${onText(module.objectives)}` : "",
].filter(Boolean).join("\n\n");

const onLessonContent = (lesson) => [
    onLines([
        ["Tipo", lesson?.type],
        ["Estado", lesson?.status],
        ["Duración", lesson?.durationMinutes ? `${lesson.durationMinutes} min` : ""],
        ["Recurso", lesson?.resourceUrl],
    ]),
    onText(lesson?.description),
    onText(lesson?.content),
    onText(lesson?.instructorNotes) ? `## Notas del instructor\n\n${onText(lesson.instructorNotes)}` : "",
].filter(Boolean).join("\n\n");

const onTaskContent = (task) => [
    onLines([
        ["Tipo", task?.type],
        ["Estado", task?.status],
        ["Prioridad", task?.priority],
        ["Vence", task?.dueDate ? onDate(task.dueDate) : ""],
    ]),
    onText(task?.description),
    onText(task?.notes) ? `## Notas\n\n${onText(task.notes)}` : "",
].filter(Boolean).join("\n\n");

const onScheduleContent = (event) => [
    onLines([
        ["Tipo", event?.type],
        ["Estado", event?.status],
        ["Fecha", event?.scheduledAt ? onDate(event.scheduledAt) : ""],
        ["Duración", event?.durationMinutes ? `${event.durationMinutes} min` : ""],
        ["Lugar", event?.location],
        ["Enlace", event?.meetingUrl],
    ]),
    onText(event?.description),
    onText(event?.notes) ? `## Notas\n\n${onText(event.notes)}` : "",
].filter(Boolean).join("\n\n");

export const buildCourseExportTree = (course = {}) => {
    const modules = Array.isArray(course?.modules) ? course.modules : [];
    const tasks = Array.isArray(course?.tasks) ? course.tasks : [];
    const schedule = Array.isArray(course?.schedule) ? course.schedule : [];

    const tree = [];

    tree.push({
        id: "course-info",
        title: "Ficha del curso",
        content: [
            onLines([
                ["Estado", course?.status],
                ["Nivel", course?.level],
                ["Modalidad", course?.modality],
                ["Institución / plataforma", course?.platform],
                ["Inicio", course?.startDate],
                ["Fin estimado", course?.endDate],
            ]),
            onText(course?.description) ? `## Descripción\n\n${onText(course.description)}` : "",
            onText(course?.audience) ? `## A quién se lo voy a enseñar\n\n${onText(course.audience)}` : "",
            onText(course?.objectives) ? `## Qué quiero que aprendan\n\n${onText(course.objectives)}` : "",
            onText(course?.prerequisites) ? `## Qué deberían saber antes\n\n${onText(course.prerequisites)}` : "",
        ].filter(Boolean).join("\n\n"),
        children: [],
    });

    modules.forEach((module, moduleIndex) => {
        tree.push({
            ...module,
            id: module?.id || `module-${moduleIndex + 1}`,
            title: module?.title || `Módulo ${moduleIndex + 1}`,
            content: onModuleContent(module),
            children: (module?.lessons || []).map((lesson, lessonIndex) => ({
                ...lesson,
                id: lesson?.id || `lesson-${moduleIndex + 1}-${lessonIndex + 1}`,
                title: lesson?.title || `Apunte ${lessonIndex + 1}`,
                content: onLessonContent(lesson),
                children: [],
            })),
        });
    });

    if (tasks.length) {
        tree.push({
            id: "course-tasks",
            title: "Tareas",
            content: "",
            children: tasks.map((task, index) => ({
                ...task,
                id: task?.id || `task-${index + 1}`,
                title: task?.title || `Tarea ${index + 1}`,
                content: onTaskContent(task),
                children: [],
            })),
        });
    }

    if (schedule.length) {
        tree.push({
            id: "course-schedule",
            title: "Itinerario",
            content: "",
            children: schedule.map((event, index) => ({
                ...event,
                id: event?.id || `schedule-${index + 1}`,
                title: event?.title || `Evento ${index + 1}`,
                content: onScheduleContent(event),
                children: [],
            })),
        });
    }

    return tree;
};

export const downloadCoursePdf = (course) => downloadPdfTree({
    title: course?.title || "Curso",
    description: course?.description || "",
    tree: buildCourseExportTree(course),
});

export const downloadCourseMarkdown = (course) => downloadMarkdownTreeZip({
    title: course?.title || "Curso",
    description: course?.description || "",
    tree: buildCourseExportTree(course),
});
